// src/components/MessageInput.jsx
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import './MessageInput.css';
import PropTypes from 'prop-types';
import autoSuggestionConfig from '../config/autoSuggestionConfig';
import StatusNotification from './StatusNotification';

const getSupportedMimeType = () => {
  const possibleTypes = [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/mpeg'
  ];

  for (const mimeType of possibleTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return '';
};

const MessageInput = forwardRef(({ 
  onSend, 
  onImageUpload, 
  onSendButtonVisibilityChange, 
  messages, 
  onSuggestionsUpdate,
  placeholder 
}, ref) => {
  const [input, setInput] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [pendingPdfUrl, setPendingPdfUrl] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const audioChunksRef = useRef([]);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldShowSendButton = () => {
    return input.trim().length > 0 || uploadedImageUrl !== '' || pendingPdfUrl !== '' || isRecording;
  };

  const handleRegenerate = async () => {
    // Find the last user message
    const lastUserMessageIndex = messages.length - 2;
    if (lastUserMessageIndex >= 0 && messages[lastUserMessageIndex].role === 'user') {
      // Re-send the last user message
      await onSend(
        messages[lastUserMessageIndex].content,
        false, // Don't force web search
        true // Flag to indicate this is a regeneration
      );
    }
  };

  const sendMessage = async () => {
    const messageText = input.trim();

    if (isRecording) {
      // Stop recording and send the transcribed message after transcription
      await stopRecording(true); // Pass 'true' to indicate we want to send after stopping
      return;
    }

    if (messageText.length === 0 && uploadedImageUrl === '' && pendingPdfUrl === '') {
      // Do not send empty messages
      return;
    }
    setInput('');
    setThumbnailUrl('');
    setUploadedImageUrl('');
    setPendingPdfUrl('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    await onSend(messageText, webSearchEnabled);
    setWebSearchEnabled(false); // Reset after sending
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    console.log('File selected:', selectedFile);
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf' || selectedFile.type.startsWith('image/')) {
        const result = await handleFileUpload(selectedFile);
        if (result) {
          console.log('File uploaded successfully');
        }
      }
      e.target.value = null;
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const data = await response.json();
      const fileUrl = data.imageUrl;

      // Set URL based on file type
      if (file.type.startsWith('image/')) {
        setUploadedImageUrl(fileUrl);
        setPendingPdfUrl('');
      } else if (file.type === 'application/pdf') {
        setPendingPdfUrl(fileUrl);
        setUploadedImageUrl('');
      }

      // Call handleImageUpload with the URL and isPdf flag
      onImageUpload(fileUrl, file.type === 'application/pdf');
      return true;
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('File upload failed.');
      return false;
    }
  };

  const stitchCanvasesVertically = (canvases) => {
    const totalHeight = canvases.reduce((sum, canvas) => sum + canvas.height, 0);
    const maxWidth = Math.max(...canvases.map((canvas) => canvas.width));

    const stitchedCanvas = document.createElement('canvas');
    stitchedCanvas.width = maxWidth;
    stitchedCanvas.height = totalHeight;
    const context = stitchedCanvas.getContext('2d');

    let yOffset = 0;
    for (const canvas of canvases) {
      context.drawImage(canvas, 0, yOffset);
      yOffset += canvas.height;
    }

    return stitchedCanvas;
  };

  const handleTextareaInput = (e) => {
    if (isRecording) {
      // If user starts typing while recording, stop the recording
      stopRecording(false); // Do not send after stopping
    }

    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
  };

  const processSuggestions = async (messages) => {
    try {
      // Check if regeneration is possible
      const canRegenerate = messages.length >= 2 && 
                           messages[messages.length - 1].role === 'assistant';

      const suggestionMessages = [];
      
      if (autoSuggestionConfig.includeSystemMessage) {
        suggestionMessages.push({
          role: 'system',
          content: autoSuggestionConfig.systemMessage,
        });
      }

      // Add context messages with better content handling
      if (autoSuggestionConfig.includeContext) {
        suggestionMessages.push(...messages.map(msg => {
          // Handle array content (for messages with media)
          if (Array.isArray(msg.content)) {
            const textContent = msg.content
              .filter(item => item.type === 'text')
              .map(item => item.text)
              .join(' ');
            
            return {
              role: msg.role,
              content: textContent || 'file provided'  // Use 'file provided' if no text content
            };
          }
          
          // Handle string content
          return {
            role: msg.role,
            content: msg.content
          };
        }));
      }

      // Add predefined user message
      suggestionMessages.push({
        role: 'user',
        content: autoSuggestionConfig.predefinedUserMessage
      });

      console.log('Processing suggestions with messages:', suggestionMessages);

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: suggestionMessages,
          model: autoSuggestionConfig.model,
          temperature: autoSuggestionConfig.temperature,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get suggestions');
      }

      const data = await response.json();
      const suggestionText = data.choices[0].message.content;
      
      console.log('Received suggestion response:', suggestionText);

      // Extract suggestions from brackets - now take all 3
      const suggestionMatches = suggestionText.match(/\[(.*?)\]/g);
      let suggestions = [];
      
      if (suggestionMatches) {
        suggestions = suggestionMatches
          .map(s => s.replace(/[\[\]]/g, ''))
          .slice(0, 3); // Take all 3 suggestions now
      }

      // Add "Regenerate Response" as an additional suggestion if possible
      if (canRegenerate) {
        suggestions.push('Regenerate Response');
      }

      onSuggestionsUpdate(suggestions);
    } catch (error) {
      console.error('Error processing suggestions:', error);
      onSuggestionsUpdate([]);
    }
  };

  const handleWebSearchToggle = () => {
    setWebSearchEnabled(!webSearchEnabled);
    setShowNotification(true);
    // Hide notification after 2 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    
    if (!items) return;

    for (const item of items) {
      if (item.type.indexOf('image') === 0) {
        e.preventDefault();
        
        const file = item.getAsFile();
        if (!file) continue;

        try {
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await fetch('/api/upload', {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to upload pasted image');
          }

          const data = await response.json();
          setUploadedImageUrl(data.imageUrl);
          onImageUpload(data.imageUrl, false);
        } catch (error) {
          console.error('Error uploading pasted image:', error);
          alert('Failed to upload pasted image');
        }
      }
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      // Determine supported mimeType
      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        alert('Your browser does not support audio recording.');
        return;
      }
      console.log('Using mimeType:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      audioChunksRef.current = [];

      mediaRecorder.onstart = () => {
        console.log('MediaRecorder started');
      };

      mediaRecorder.ondataavailable = (event) => {
        console.log('ondataavailable triggered');
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('Data chunk added, size:', event.data.size);
        } else {
          console.log('Empty data chunk received');
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
      };

      mediaRecorder.start();
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('Error accessing microphone. Please ensure you have granted microphone permissions.');
    }
  };

  const stopRecording = async (shouldSend = false) => {
    if (mediaRecorderRef.current && isRecording) {
      setIsRecording(false);

      mediaRecorderRef.current.onstop = async () => {
        console.log('MediaRecorder stopped');
        // Stop all tracks to release the microphone
        if (recordingStreamRef.current) {
          recordingStreamRef.current.getTracks().forEach(track => track.stop());
          recordingStreamRef.current = null;
        }

        // Combine audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Audio Blob size:', audioBlob.size);

        // Optional: You can log or check the chunks
        console.log('Audio chunks:', audioChunksRef.current);

        if (audioBlob.size === 0) {
          console.error('Recorded audio blob is empty');
          alert('Failed to capture audio. Please try again.');
          return;
        }

        // Send the audioBlob to the server for transcription
        const formData = new FormData();
        formData.append('file', audioBlob, 'recording.webm');

        try {
          const response = await fetch('/api/transcribe', {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Failed to transcribe audio');
          }

          const data = await response.json();
          const transcription = data.transcription;

          if (shouldSend && transcription.trim().length > 0) {
            // Send the transcription directly
            setInput(''); // Clear input
            await onSend(transcription, webSearchEnabled);
            setWebSearchEnabled(false); // Reset after sending
          } else {
            // Place the transcription into the text area without sending
            setInput(transcription);

            // Adjust textarea height
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
              textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
            }
          }

        } catch (error) {
          console.error('Error transcribing audio:', error);
          alert('Failed to transcribe audio.');
        }
      };

      mediaRecorderRef.current.stop();
    }
  };

  const handleMicButtonClick = () => {
    if (isRecording) {
      stopRecording(false); // Do not send after stopping
    } else {
      startRecording();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    setIsFocused(false);
  };

  const handleClearInput = () => {
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '48px'; // Reset to initial height
      textareaRef.current.focus(); // Keep focus on textarea
    }
  };

  useEffect(() => {
    onSendButtonVisibilityChange(shouldShowSendButton());
  }, [input, thumbnailUrl, isRecording, onSendButtonVisibilityChange]);

  useImperativeHandle(ref, () => ({
    processSuggestions: (messages) => processSuggestions(messages)
  }));

  return (
    <div className="message-input-container">
      <StatusNotification 
        message={webSearchEnabled ? "Web Search Enabled" : "Web Search Disabled"}
        isVisible={showNotification}
      />
      <div className={`message-input ${isExpanded ? 'focused' : ''}`}>
        {isExpanded && (
          <button 
            className="collapse-button"
            onClick={handleCollapse}
            title="Show buttons"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}

        {/* Show either image or PDF thumbnail */}
        {uploadedImageUrl && (
          <div 
            className="thumbnail-preview"
            onClick={() => setUploadedImageUrl('')}
            title="Click to remove image"
          >
            <img 
              src={uploadedImageUrl} 
              alt="Upload preview" 
              className="thumbnail-image"
            />
          </div>
        )}
        {pendingPdfUrl && (
          <div 
            className="thumbnail-preview pdf"
            onClick={() => setPendingPdfUrl('')}
            title="Click to remove PDF"
          >
            <span className="pdf-text">PDF</span>
          </div>
        )}

        <div className="buttons-container">
          {/* Microphone button */}
          <label 
            className={`mic-button ${isRecording ? 'recording' : ''}`} 
            onClick={handleMicButtonClick}
            title={isRecording ? 'Stop recording' : 'Start recording'}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </label>

          {/* Update the file upload button */}
          <div className={`file-upload-container ${uploadedImageUrl || pendingPdfUrl ? 'has-file' : ''}`}>
            {/* Move the input outside the label and hide it */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label 
              className="file-upload-label" 
              onClick={() => fileInputRef.current.click()}
              title="Upload file"
            >
              {/* SVG icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </label>
          </div>

          {/* Existing web search button */}
          <div className="web-search-container">
            <label 
              className={`web-search-label ${webSearchEnabled ? 'enabled' : ''}`}
              onClick={handleWebSearchToggle}
              title="Enable web search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </label>
          </div>
        </div>

        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleTextareaInput}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !('ontouchstart' in window)) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder={placeholder}
          rows="1"
        />

        <button
          className={`clear-input-button ${input.length > 0 ? 'visible' : ''}`}
          onClick={handleClearInput}
          title="Clear input"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <button
          className={`floating-send-button ${shouldShowSendButton() ? 'visible' : ''}`}
          onClick={sendMessage}
          disabled={!shouldShowSendButton()}
          title="Send"
        >
          {/* Send icon */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5 12 12 5 19 12"></polyline>
          </svg>
        </button>

      </div>
    </div>
  );
});

MessageInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onSendButtonVisibilityChange: PropTypes.func.isRequired,
  messages: PropTypes.array.isRequired,
  onSuggestionsUpdate: PropTypes.func.isRequired,
};

export default MessageInput;
